// Renders the customer and order details panel.

import { useRef, useState } from 'react';
import { Frown, PanelRight } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn, initialsOf } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type {
  SimilarTicketMatch,
  TraceStep,
  WorkspaceCustomerDto,
  WorkspaceStorefrontDto,
  WorkspaceSubscriptionDto,
  PendingActionsDto,
} from './types';
import { SimilarTicketsCard } from './similar-tickets-card';
import { CustomerTicketsCard } from './customer-tickets-card';
import { StorefrontCard } from './storefront-card';
import { SubscriptionCard } from './subscription-card';
import { ToolCallsCard } from './tool-calls-card';
import { PendingActionsCard } from './pending-actions-card';

interface DetailsPanelProps {
  customer: WorkspaceCustomerDto;
  storefront: WorkspaceStorefrontDto;
  subscriptions: WorkspaceSubscriptionDto[];
  trace: TraceStep[];
  /** Omitted until the ticket's turns name a RAG match — the card needs all of it or none. */
  similarTickets?: {
    matches: SimilarTicketMatch[];
    activeConversationId: string | null;
    onOpenTicket: (match: SimilarTicketMatch) => void;
  };
  /** Whether Kim answered this ticket on its own; the card says so either way. Omitted while unknown. */
  automated?: boolean;
  /** The Sentinel ticket whose customer history the panel can open; omitted where there is none. */
  customerTicketsFor?: number;
  /**
   * The tag / close / status change waiting on this ticket, with the callback that runs them.
   * Omitted when the turn has none, or when the viewer cannot act on the ticket.
   */
  pendingActions?: {
    actions: PendingActionsDto;
    onRun?: () => Promise<void>;
  };
  onCollapse: () => void;
  /** Omitted where the panel is not resizable; it then sits at its own width. */
  width?: number;
  onWidthChange?: (width: number) => void;
}

/** Its own width, and half again. Held by the workspace so a ticket change keeps it. */
export const DETAILS_MIN_WIDTH = 340;
export const DETAILS_MAX_WIDTH = DETAILS_MIN_WIDTH * 1.5;

export function DetailsPanel({
  customer,
  storefront,
  subscriptions,
  trace,
  similarTickets,
  customerTicketsFor,
  automated,
  pendingActions,
  onCollapse,
  width = DETAILS_MIN_WIDTH,
  onWidthChange,
}: DetailsPanelProps) {
  const toolCalls = trace.filter((step) => step.toolName);
  const panel = useRef<HTMLDivElement>(null);
  // Where the drag started, so the width follows the pointer rather than jumping to it.
  const drag = useRef<{ x: number; width: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  // Commits the width the node is showing and disarms the drag. Called from every way a
  // drag can end, because a release we never see would leave hovering able to resize.
  const endDrag = () => {
    if (!drag.current) return;
    drag.current = null;
    setDragging(false);
    if (panel.current) onWidthChange?.(parseFloat(panel.current.style.width));
  };

  return (
    <div
      ref={panel}
      // Shrinkable, floored at its own width: when the rail opens there is less room to
      // share, and the panel gives it back rather than squeezing the conversation.
      style={{ width, minWidth: DETAILS_MIN_WIDTH }}
      className="relative flex h-full flex-col border-l border-gray-500 bg-white"
    >
      {/* Dragging left widens the panel. The grab area straddles the border and is wider
          than the line it draws, so the pointer does not keep slipping off it. */}
      {onWidthChange && (
        <div
          role="separator"
          aria-orientation="vertical"
          className={cn(
            'absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize touch-none select-none',
            'after:absolute after:inset-y-0 after:left-1/2 after:w-px after:-translate-x-1/2',
            dragging ? 'after:bg-gray-700' : 'hover:after:bg-gray-700'
          )}
          onPointerDown={(event) => {
            // Stops the drag from selecting the text it passes over.
            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);
            drag.current = { x: event.clientX, width };
            setDragging(true);
          }}
          onPointerMove={(event) => {
            if (!drag.current || !panel.current) return;
            // The held button is the truth: no button means the release went somewhere
            // else, and without this a plain hover would go on resizing the panel.
            if (event.buttons === 0) {
              endDrag();
              return;
            }
            const next = drag.current.width - (event.clientX - drag.current.x);
            // Written straight to the node: re-rendering every card on each move stutters.
            panel.current.style.width = `${Math.min(DETAILS_MAX_WIDTH, Math.max(DETAILS_MIN_WIDTH, next))}px`;
          }}
          onPointerUp={(event) => {
            event.currentTarget.releasePointerCapture(event.pointerId);
            endDrag();
          }}
          onPointerCancel={endDrag}
          onLostPointerCapture={endDrag}
        />
      )}
      {/* The customer is the header: same 60px as the thread's, collapse on the same line. */}
      <div className="flex h-[60px] shrink-0 items-center justify-between gap-2 border-b border-gray-500 bg-white/80 pl-4 pr-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <Avatar className="size-9 shrink-0 rounded-full border border-border bg-violet-50">
            <AvatarFallback className="rounded-full bg-violet-50 text-[13px] font-medium text-violet-500">
              {initialsOf(customer.name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <p className="truncate text-[14px] font-semibold leading-[18px] text-text-body">{customer.name}</p>
              {automated !== undefined && (
                <span
                  className={cn(
                    'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium leading-4',
                    automated ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-800',
                  )}
                >
                  {automated ? 'Automation' : 'Handled by a person'}
                </span>
              )}
            </div>
            {customer.email && (
              <a
                href={`mailto:${customer.email}`}
                className="block truncate text-[12px] leading-4 text-gray-700 hover:text-text-body hover:underline"
              >
                {customer.email}
              </a>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {customer.segment && (
            <span className="flex items-center gap-1.5 rounded-full bg-red-50 px-1.5 py-0.5">
              <Frown className="size-3 text-red-500" />
              <span className="text-[12px] leading-4 text-red-500">{customer.segment}</span>
            </span>
          )}
          <Button variant="ghost" size="icon-sm" onClick={onCollapse} className="text-gray-900" aria-label="Collapse details">
            <PanelRight className="size-[14px]" />
          </Button>
        </div>
      </div>

      {/* The header draws the line under it, so the first card's own top line is hidden. */}
      <div className="custom-scrollbar flex-1 overflow-y-auto [scrollbar-gutter:stable] [&>:first-child]:hidden">
        {pendingActions && (
          <>
            <div className="h-px w-full bg-gray-500" />
            <PendingActionsCard actions={pendingActions.actions} onRun={pendingActions.onRun} />
          </>
        )}

        {toolCalls.length > 0 && (
          <>
            <div className="h-px w-full bg-gray-500" />
            <ToolCallsCard steps={toolCalls} />
          </>
        )}

        {/* Shopify stays visible even when empty, so the panel never looks blank. */}
        <div className="h-px w-full bg-gray-500" />
        <StorefrontCard storefront={storefront} />

        {subscriptions
          .filter((subscription) => subscription.items.length > 0)
          .map((subscription) => (
            <div key={subscription.provider}>
              <div className="h-px w-full bg-gray-500" />
              <SubscriptionCard subscription={subscription} />
            </div>
          ))}

        {similarTickets && similarTickets.matches.length > 0 && (
          <>
            <div className="h-px w-full bg-gray-500" />
            <SimilarTicketsCard
              matches={similarTickets.matches}
              activeConversationId={similarTickets.activeConversationId}
              onOpenTicket={similarTickets.onOpenTicket}
            />
          </>
        )}

        {customerTicketsFor !== undefined && (
          <>
            <div className="h-px w-full bg-gray-500" />
            <CustomerTicketsCard ticketId={customerTicketsFor} />
          </>
        )}

        {/* Closes the card stack so the last card is bounded from below too. */}
        <div className="h-px w-full bg-gray-500" />
      </div>
    </div>
  );
}
